import * as React from 'react';
import { useEffect, useState } from 'react';
import { Columns, Column } from 'bloomer';
import { ApolloQueryResult } from 'apollo-client';
import Container from '../molecules/Container';
import Header from '../molecules/header';
import Footer from '../molecules/Footer';
import Loading from '../atoms/Loading';
import RegistrationForm from '../organisms/registrationPage/RegistrationForm';
import { GET_REGISTRATION_DATA } from '../apollo/queries/RegistrationData';
import ApiClient from '../apollo/ApiClient';
import {
  CountryCodeProps,
  RegistrationData,
  QueryAttributes,
} from '../../interfaces/Registration';
import CountrySelector from '../atoms/registrationPage/CountrySelector';
import { countryOptions } from '../lib/Helpers';
import { Config } from '../../config';
import { cookies } from '../lib/Storage';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import {
  logFirebaseEvent,
  trackRegisterUrlFrom,
} from '../../utils/FirebaseEventTracker';
import { getBaseUrl } from '../../utils/isInternalLink';
import { setLoginVisitEvent } from '../../utils/DataLayer';

const AVAILABLE_COUNTRIES = ['id', 'ph'];

import './Registration.scss';

const RegistrationPage: React.FC<CountryCodeProps & WrappedComponentProps> = ({
  countryCode,
  intl,
}: CountryCodeProps & WrappedComponentProps): React.ReactElement | null => {
  const [registrationData, setRegistrationData] =
    useState<QueryAttributes | null>(null);
  // VP-01c: captured once on mount, via a lazy initializer, so it survives
  // both the country-code coercion below (which rewrites the URL and would
  // otherwise wipe the query string before a later render re-reads it) and
  // any re-render this component goes through afterwards. This is the ONE
  // source of truth for the invite payload — nothing else re-reads the URL.
  const [invitePayload] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('data'),
  );
  // VP-01c: presence of a payload is not the same as it being a genuine
  // invite — GET_REGISTRATION_DATA below is the actual validation. Do not
  // render the form until that validation has actually passed.
  const [payloadState, setPayloadState] = useState<'checking' | 'valid'>(
    'checking',
  );

  document.title = intl.formatMessage({
    id: 'page_title.sign_up',
    defaultMessage: 'Sign up | Quipper',
  });

  const token = cookies.get(Config.authTokenCookieName);
  const loginReturnUrl = getBaseUrl(cookies.get('loginReturnUrl')) || '';

  const needsCountryCoercion = !AVAILABLE_COUNTRIES.includes(
    countryCode as string,
  );
  const effectiveCountryCode = needsCountryCoercion ? 'id' : countryCode;

  useEffect((): void => {
    if (trackRegisterUrlFrom.includes(loginReturnUrl)) {
      logFirebaseEvent('REGISTRATION_OPEN');
      cookies.set('trackRegistrationFrom', true);
    } else {
      cookies.remove('trackRegistrationFrom');
    }
    // VP-01c: report the country the page actually renders as, not the raw
    // route param — the coercion below rewrites 'vn' and friends to 'id',
    // and this used to read the coerced value via the old in-render mutation
    // that effectiveCountryCode replaced.
    setLoginVisitEvent(effectiveCountryCode);
  }, []);

  // Populate Form with Data from Query Params (Encoded JSON String)
  useEffect((): void => {
    if (!invitePayload) {
      return;
    }

    ApiClient.query({
      query: GET_REGISTRATION_DATA,
      variables: {
        data: invitePayload,
      },
    })
      .then((result: ApolloQueryResult<RegistrationData>): void => {
        setRegistrationData(result.data.registrationData);
        setPayloadState('valid');
      })
      // VP-01c: an invite payload that does not decrypt/validate is not a
      // genuine invite. Never fall through to a rendered form — send it to
      // /invalid, which explains that the link expired and links on to
      // Learn, same as the failure path educator-api already uses
      // (organization_memberships_controller.rb#invalid_url).
      .catch((): void => window.location.replace('/invalid'));
  }, [invitePayload]);

  useEffect((): void => {
    if (!needsCountryCoercion) {
      return;
    }
    // VP-01c: correcting the URL, not navigating — replaceState so Back
    // doesn't have to step through it, and in an effect so a StrictMode
    // double-render doesn't do it twice. Query string preserved — dropping
    // it here would discard the invite payload.
    window.history.replaceState(
      null,
      document.title,
      `/signup/id${window.location.search}`,
    );
  }, [needsCountryCoercion]);

  // VP-01c: navigation is a side effect and must not run during render (React
  // 18 StrictMode double-invokes renders, and a render-body navigation call
  // fires twice). Decide here, act in the effect below.
  const mustLeave = !!token || !invitePayload;

  useEffect((): void => {
    if (!!token) {
      window.location.assign(Config.learnUrl);
      return;
    }
    // VP-01c: no invite payload => not the school-code flow => no form.
    if (!invitePayload) {
      window.location.replace(`${Config.learnUrl}/login`);
    }
  }, [token, invitePayload]);

  if (mustLeave) {
    return null;
  }

  // VP-01c: payload presence confirmed above; validity is still pending.
  if (payloadState === 'checking') {
    return <Loading />;
  }

  const countryCodeParamRaw =
    !!registrationData &&
    registrationData.countryCode &&
    registrationData.countryCode
      ? registrationData.countryCode.toLowerCase()
      : undefined;
  // VP-01c: the payload's country isn't attacker-controlled (it's encrypted
  // server-side), but it also isn't validated against what this form actually
  // supports — fall back to 'id' rather than passing an unsupported code
  // (e.g. 'vn') straight through to RegistrationForm.
  const countryCodeParam = countryCodeParamRaw
    ? AVAILABLE_COUNTRIES.includes(countryCodeParamRaw)
      ? countryCodeParamRaw
      : 'id'
    : undefined;
  const defaultCountryCode = !!countryCodeParam
    ? (countryCodeParam as 'id' | 'ph' | undefined)
    : effectiveCountryCode;

  return (
    <div className="Registration">
      <Header>
        {/* VP-01c: the invite payload wins over the route param above, so a
            switch here would move the URL without moving the form. Lock the
            control only when the payload actually carries a country; leave
            it usable otherwise. */}
        <CountrySelector
          name="country"
          defaultValue={defaultCountryCode}
          options={countryOptions}
          disabled={!!countryCodeParam}
        />
      </Header>

      <Container className="Registration__section">
        <Columns>
          <Column
            isSize={{ tablet: 10, desktop: 8, widescreen: 8 }}
            isOffset={{ tablet: 1, desktop: 2, widescreen: 2 }}
          >
            <RegistrationForm
              countryCode={defaultCountryCode}
              defaultValues={registrationData}
            />
          </Column>
        </Columns>
      </Container>

      <Footer />
    </div>
  );
};

export default injectIntl(RegistrationPage);
